import { useRef } from "react";
import { contact } from "../../models/site";
import { ExternalLink } from "./ExternalLink";
import { PageIntro } from "./PageIntro";

const youBikeStations =
  "https://www.youbike.com.tw/region/taitung/stations/list/";
const recommendedTaxi = {
  name: "小蔡司機",
  phone: "+886989766709",
  displayPhone: "0989 766 709",
  profile: "https://share.google/BU63XOoegTAlQJnwN",
};

const modes = [
  { href: "#bus", index: "01", zh: "公車", en: "BUS" },
  { href: "#bike", index: "02", zh: "單車", en: "YOUBIKE" },
  { href: "#driving", index: "03", zh: "自行開車", en: "BY CAR" },
  { href: "#taxi", index: "04", zh: "計程車", en: "BY TAXI" },
];

export function DirectionPage() {
  const taxiDialog = useRef<HTMLDialogElement>(null);

  return (
    <div className="standard-page transport-page">
      <PageIntro eyebrow="GETTING HERE" title="交通方式" en="DIRECTION">
        <p>
          從抵達台東到走進都蘭，依照行李、時間與旅行步調，選擇最適合你的交通方式。
          <span className="transport-en">
            Choose the route that best suits your luggage and schedule.
          </span>
        </p>
      </PageIntro>

      <section className="taitung-gateway">
        <div>
          <p className="eyebrow">FIRST, GET TO TAITUNG</p>
          <h2>如何到台東？</h2>
        </div>
        <div className="taitung-gateway-content">
          <p>
            搭火車、飛機、客運或自行開車，先從這裡查看抵達台東的完整說明。
            <span className="transport-en">
              See how to reach Taitung by train, plane, coach or car.
            </span>
          </p>
          <a className="outline-dark" href="/getting-to-taitung">
            查看如何到台東 GETTING TO TAITUNG →
          </a>
        </div>
      </section>

      <section className="local-transport-intro">
        <div>
          <p className="eyebrow">TAITUNG TO DULAN STAR</p>
          <h2>從台東到小星</h2>
        </div>
        <p>
          抵達台東後，可依照行李、時間與旅行步調，選擇公車、YouBike、自行開車或計程車前往。
          <span className="transport-en">
            From Taitung, continue by bus, YouBike, car or taxi.
          </span>
        </p>
      </section>

      <nav
        className="transport-quick-nav"
        aria-label="從台東到小星的交通方式 Transport from Taitung to Dulan Star"
      >
        {modes.map((mode) => (
          <a href={mode.href} key={mode.href}>
            <span>{mode.index}</span>
            <strong>{mode.zh}</strong>
            <small>{mode.en}</small>
          </a>
        ))}
      </nav>

      <div className="transport-methods">
        <article className="transport-method" id="bus">
          <header>
            <span>01</span>
            <p className="eyebrow">BY BUS</p>
            <h2>搭公車</h2>
          </header>
          <div className="transport-method-content">
            <p className="transport-lead">
              從台東轉運站或台東火車站搭乘往成功、靜埔、泰源方向的東海岸線公車，在「下都蘭」站下車。
            </p>
            <p>
              下車前一站是「水往上流」。下車後步行約 3–5 分鐘。
              <span className="transport-en">
                Take an East Coast bus from Taitung Bus or Train Station and get
                off at Xia Dulan, one stop after Water Running Up. Walk 3–5
                minutes to the guest house.
              </span>
            </p>
            <p className="transport-caution">
              路況可能造成提早或延誤，請提前 10–15
              分鐘到站候車，出發當日務必查看公車即時動態。
              <span className="transport-en">
                Buses may run early or late. Arrive 10–15 minutes early and
                check live updates.
              </span>
            </p>
            <a className="outline-dark" href="/timetable">
              查看公車時刻表 BUS TIMETABLE
            </a>
          </div>
        </article>

        <article className="transport-method" id="bike">
          <header>
            <span>02</span>
            <p className="eyebrow">BY YOUBIKE</p>
            <h2>單車漫遊</h2>
          </header>
          <div className="transport-method-content">
            <p className="transport-lead">
              想用自己的速度感受東海岸，可在台東市區借用
              YouBike，於都蘭舊糖廠站還車，再步行前往小星。
            </p>
            <p>
              台東到都蘭為約 20
              多公里的長距離騎乘，沿途部分路段與汽機車共用；請依體力、日照與風勢評估，出發前確認兩端站點的車輛及空位。
              <span className="transport-en">
                Rent a YouBike in Taitung and return it at Dulan Sugar Factory.
                The 20+ km ride shares some roads with traffic, so check weather,
                daylight, bikes and docks first.
              </span>
            </p>
            <ExternalLink className="outline-dark" href={youBikeStations}>
              查詢 YOUBIKE 站點 FIND YOUBIKE STATIONS
            </ExternalLink>
          </div>
        </article>

        <article className="transport-method" id="driving">
          <header>
            <span>03</span>
            <p className="eyebrow">BY CAR</p>
            <h2>自行開車</h2>
          </header>
          <div className="transport-method-content">
            <p className="transport-lead">
              自行開車最適合攜帶較多行李，或預計沿途停留東海岸景點的旅人。
            </p>
            <p>
              抵達都蘭後請直接依 Google Maps 導航指示前往。
              <span className="transport-en">
                Best for luggage or coastal stops. Follow Google Maps to Dulan
                Star Guest House
              </span>
            </p>
            <ExternalLink className="outline-dark" href={contact.map}>
              開啟導航 OPEN GOOGLE MAPS
            </ExternalLink>
          </div>
        </article>

        <article className="transport-method" id="taxi">
          <header>
            <span>04</span>
            <p className="eyebrow">BY TAXI</p>
            <h2>搭計程車</h2>
          </header>
          <div className="transport-method-content">
            <p className="transport-lead">
              從台東火車站或市區可直接前往小星，車程通常約 25–35 分鐘。
            </p>
            <p>
              費用依跳表、時段與叫車方式為準，建議上車前先確認；可直接將 Google
              Maps 目的地交給司機查看。
              <span className="transport-en">
                A taxi from Taitung Station or the city takes about 25–35
                minutes. Confirm the fare and show the driver our Google Maps
                location.
              </span>
            </p>
            <div className="transport-actions">
              <ExternalLink className="text-link" href={contact.map}>
                交給司機查看位置<br />SHOW DRIVER THE LOCATION ↗
              </ExternalLink>
              <button
                className="outline-dark taxi-info-trigger"
                type="button"
                onClick={() => taxiDialog.current?.showModal()}
              >
                小黃資訊 TAXI INFO
              </button>
            </div>
          </div>
        </article>
      </div>

      <dialog
        ref={taxiDialog}
        className="taxi-dialog"
        aria-labelledby="taxi-dialog-title"
        onClick={(event) =>
          event.target === event.currentTarget && event.currentTarget.close()
        }
      >
        <div className="taxi-dialog-content">
          <button
            className="taxi-dialog-close"
            type="button"
            aria-label="關閉小黃資訊 Close taxi information"
            onClick={() => taxiDialog.current?.close()}
          >
            ×
          </button>
          <p className="eyebrow">LOCAL TAXI INFO</p>
          <h2 id="taxi-dialog-title">小黃資訊</h2>
          <p className="taxi-dialog-intro">
            以下是小星認識的司機資訊，旅客可依自己的行程自由決定是否聯絡。
            <span className="transport-en">
              Here is the contact information for a driver we know. You are
              welcome to decide whether he fits your travel plans.
            </span>
          </p>
          <div className="taxi-dialog-details">
            <div>
              <h3>
                {recommendedTaxi.name}
                <span>MR. TSAI</span>
              </h3>
              <a
                className="taxi-dialog-phone"
                href={`tel:${recommendedTaxi.phone}`}
              >
                {recommendedTaxi.displayPhone}
              </a>
              <ExternalLink
                className="text-link"
                href={recommendedTaxi.profile}
              >
                查看車行資訊 DRIVER INFO ↗
              </ExternalLink>
            </div>
            <p>
              台東火車站 ↔ 小星
              <span>TAITUNG STATION ↔ DULAN STAR</span>
              <strong>單趟接駁 NT$520</strong>
              <small>ONE-WAY TRANSFER</small>
            </p>
          </div>
          <p className="taxi-dialog-note">
            可直接聯絡小蔡司機；來電時告知「我是都蘭小星的客人」，並提前確認接送時間與費用。
            <span className="transport-en">
              You may contact Mr. Tsai directly. Mention that you are a Dulan
              Star guest, and confirm the pickup time and fare in advance.
            </span>
          </p>
        </div>
      </dialog>
    </div>
  );
}
